/**
 * Registro central de widgets customizados por cliente.
 *
 * Cada cliente registra seus próprios widgets chamando registerCustomWidget().
 * O ComponentRenderer e a ComponentPalette consultam este registry para
 * exibir apenas os widgets do tenant ativo (VITE_TENANT_ID).
 */

import type { ComponentType } from 'react'

export interface CustomWidgetMeta {
  /** Identificador único do tipo, ex: 'acme-kpi-card'. Use prefixo do cliente para evitar colisões. */
  type: string
  /** Nome exibido na palette do editor */
  name: string
  /** Descrição exibida na palette do editor */
  description: string
  /** Nome de ícone Lucide exibido na palette */
  icon: string
  /** ID do tenant dono deste widget. Use '*' para disponibilizar em todos os tenants. */
  tenantId: string
  /** Chave em workspace.settings que deve ser truthy para mostrar o widget. Opcional. */
  featureFlag?: string
  /** Tamanho padrão na grade ao adicionar pela palette */
  defaultSize: { w: number; h: number }
  /** Config inicial do componente ao ser criado */
  defaultConfig: Record<string, any>
  /** Componente React que renderiza o widget */
  component: ComponentType<CustomWidgetProps>
}

export interface CustomWidgetProps {
  title?: string
  config?: Record<string, any>
  allComponents?: any[]
  activeFilters?: Record<string, string>
  isCompact?: boolean
}

const registry: CustomWidgetMeta[] = []

export function registerCustomWidget(def: CustomWidgetMeta): void {
  if (registry.find((w) => w.type === def.type)) {
    console.warn(`[custom] Widget "${def.type}" já registrado — ignorando duplicata.`)
    return
  }
  registry.push(def)
}

/**
 * Retorna a definição de um widget customizado pelo tipo,
 * apenas se pertencer ao tenant ativo.
 */
export function getCustomWidget(type: string): CustomWidgetMeta | undefined {
  const tenantId = import.meta.env.VITE_TENANT_ID || 'default'
  return registry.find(
    (w) => w.type === type && (w.tenantId === tenantId || w.tenantId === '*'),
  )
}

/**
 * Retorna todos os widgets customizados disponíveis para o tenant ativo,
 * filtrando também por featureFlag nas settings do workspace quando definida.
 */
export function getCustomWidgetsForTenant(workspaceSettings?: Record<string, any>): CustomWidgetMeta[] {
  const tenantId = import.meta.env.VITE_TENANT_ID || 'default'
  return registry.filter((w) => {
    const tenantMatch = w.tenantId === tenantId || w.tenantId === '*'
    if (!tenantMatch) return false
    if (w.featureFlag) {
      return workspaceSettings?.[w.featureFlag] === true
    }
    return true
  })
}
